import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Modal from 'components/atoms/Modal'
import LaunchIcon from '@mui/icons-material/Launch'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import AlreadyUploadedFilesOfTypeModal from 'features/analytics/compontents/AlreadyUploadedFilesOfTypeModal'

const getOverview = item =>
  item?.checklistOverview || item?.checklist_overview || null

const getEntryUploadUrl = entry =>
  entry?.uploadUrl || entry?.upload_url || entry?.task?.uploadUrl || null

const getFileUrl = file =>
  file?.url || file?.originalUrl || file?.original_url || null

const getFileThumbnailUrl = file => file?.thumbnailUrl || file?.thumbnail_url

const getFileName = file =>
  file?.displayName ||
  file?.display_name ||
  file?.name ||
  file?.versions?.[0]?.name ||
  'Document'

const formatAssigneeSummary = labels => {
  if (!labels || labels.length === 0) return 'No assignee yet'
  if (labels.length === 1) return `Assigned to ${labels[0]}`
  if (labels.length === 2) return `Assigned to ${labels[0]} and ${labels[1]}`
  return `Assigned to ${labels[0]} +${labels.length - 1} more`
}

const ChecklistOverviewSummary = ({
  item,
  unitId = null,
  compact = true,
  leadingContent = null,
  filesCount = 0,
  bpId = null,
  fileTypeId = null,
  categoryId = null,
  templateId = null
}) => {
  const [open, setOpen] = useState(false)
  const overview = getOverview(item)

  const entries = useMemo(() => {
    const rawEntries = overview?.entries || []
    if (!unitId) return rawEntries

    return rawEntries.filter(entry => {
      const entryUnitId = entry?.unit?.id || null
      return !entryUnitId || String(entryUnitId) === String(unitId)
    })
  }, [overview, unitId])

  const ruleGroups = useMemo(() => {
    const groups = {}
    entries.forEach(entry => {
      const groupNo = entry?.groupNo ?? entry?.group_no ?? 0
      if (!groups[groupNo]) groups[groupNo] = { total: 0, uploaded: 0 }
      groups[groupNo].total++
      if (entry?.hasUploadedFile || entry?.has_uploaded_file) {
        groups[groupNo].uploaded++
      }
    })
    return groups
  }, [entries])

  const assignedEntries = entries.filter(
    entry => !!(entry?.assignee?.label || entry?.assignee?.email)
  )
  const totalAssigned = assignedEntries.length
  const uploadedAssigned = assignedEntries.filter(
    entry => !!(entry?.hasUploadedFile || entry?.has_uploaded_file)
  ).length
  const effectiveTotal = totalAssigned || entries.length
  const effectiveUploaded = Math.max(uploadedAssigned, Math.min(filesCount, effectiveTotal))
  const totalEntries = effectiveTotal
  const uploadedEntries = effectiveUploaded
  const unassignedCount = entries.filter(
    entry => !(entry?.assignee?.label || entry?.assignee?.email)
  ).length
  const assigneeLabels = Array.from(
    new Set(
      entries
        .map(entry => entry?.assignee?.label || entry?.assignee?.email || null)
        .filter(Boolean)
    )
  )
  const visibleEntries = useMemo(
    () =>
      entries.filter(
        entry =>
          !!(entry?.assignee?.label || entry?.assignee?.email) ||
          !!(entry?.hasUploadedFile || entry?.has_uploaded_file) ||
          !!getEntryUploadUrl(entry)
      ),
    [entries]
  )

  if (!overview || entries.length === 0) {
    return null
  }

  const handlePreview = (e, file) => {
    e.stopPropagation()
    const url = getFileUrl(file)
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleDownload = (e, file) => {
    e.stopPropagation()
    const url = getFileUrl(file)
    if (!url) return
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.target = '_blank'
    anchor.download = getFileName(file)
    anchor.click()
  }

  return (
    <>
      <SummaryWrap $compact={compact}>
        {leadingContent}
        <SummaryMetaButton
          type='button'
          onClick={e => {
            e.stopPropagation()
            setOpen(true)
          }}
          disabled={visibleEntries.length === 0}
        >
          {formatAssigneeSummary(assigneeLabels)}
        </SummaryMetaButton>
        {totalEntries > 0 && (
          bpId && fileTypeId ? (
            <AlreadyUploadedFilesOfTypeModal
              bpId={bpId}
              type={fileTypeId}
              categoryId={categoryId}
              templateId={templateId}
            >
              <FilesCount $progress={totalEntries > 0 ? (uploadedEntries / totalEntries) * 100 : 0}>
                {uploadedEntries}/{totalEntries} file{totalEntries === 1 ? '' : 's'} uploaded
              </FilesCount>
            </AlreadyUploadedFilesOfTypeModal>
          ) : (
            <FilesCount $progress={totalEntries > 0 ? (uploadedEntries / totalEntries) * 100 : 0}>
              {uploadedEntries}/{totalEntries} file{totalEntries === 1 ? '' : 's'} uploaded
            </FilesCount>
          )
        )}
      </SummaryWrap>

      {open && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title={item?.title || 'Checklist overview'}
          maxWidthDesktop='920px'
        >
          <ModalBody>
            <OverviewIntro>
              This view shows the assignment status for this checklist item.
              Upload actions only appear where an assignee or a task already
              exists.
            </OverviewIntro>
            {unassignedCount > 0 && (
              <HiddenSummary>
                {unassignedCount} entries are still unassigned, so they are
                hidden from this list until someone is assigned.
              </HiddenSummary>
            )}
            {visibleEntries.map(entry => {
              const files = entry?.uploadedFiles || entry?.uploaded_files || []
              const taskStatus = entry?.task?.status || 'not_started'
              return (
                <EntryCard
                  key={`${entry?.groupNo || entry?.group_no}-${entry?.unit?.id || 'bp'}`}
                >
                  <EntryHeader>
                    <EntryTitle>
                      {entry?.ruleName || entry?.rule_name || 'Checklist rule'}
                    </EntryTitle>
                    <StatusPill
                      $uploaded={
                        (entry?.hasUploadedFile || entry?.has_uploaded_file) ===
                        true
                      }
                    >
                      {entry?.hasUploadedFile || entry?.has_uploaded_file
                        ? 'Uploaded'
                        : 'Missing'}
                    </StatusPill>
                  </EntryHeader>
                  <EntryMeta>
                    <span>
                      {entry?.scope === 'unit'
                        ? entry?.unit?.name || 'Unit'
                        : 'Building Passport'}
                    </span>
                    <span>
                      {entry?.assignee?.label ||
                        entry?.assignee?.email ||
                        'No assignee'}
                    </span>
                    <span>{String(taskStatus).replace(/_/g, ' ')}</span>
                  </EntryMeta>
                  {(entry?.tagNames || entry?.tag_names || []).length > 0 && (
                    <TagsRow>
                      {(entry?.tagNames || entry?.tag_names || []).map(
                        tagName => (
                          <TagChip key={tagName}>{tagName}</TagChip>
                        )
                      )}
                    </TagsRow>
                  )}
                  <EntryActions>
                    {getEntryUploadUrl(entry) && (
                      <SecondaryButton
                        type='button'
                        onClick={e => {
                          e.stopPropagation()
                          try {
                            const url = new URL(getEntryUploadUrl(entry), window.location.origin)
                            url.searchParams.set('method', 'traditional')
                            window.open(url.toString(), '_blank', 'noopener,noreferrer')
                          } catch {
                            window.open(getEntryUploadUrl(entry), '_blank', 'noopener,noreferrer')
                          }
                        }}
                      >
                        <LaunchIcon style={{ fontSize: 14 }} />
                        Open upload
                      </SecondaryButton>
                    )}
                  </EntryActions>
                  {files.length > 0 ? (
                    <FilesGrid>
                      {files.map(file => (
                        <FileCard key={file.id}>
                          <FilePreviewButton
                            type='button'
                            onClick={e => handlePreview(e, file)}
                          >
                            {getFileThumbnailUrl(file) ? (
                              <FileThumb
                                src={getFileThumbnailUrl(file)}
                                alt={getFileName(file)}
                              />
                            ) : (
                              <FileThumbPlaceholder>
                                No preview
                              </FileThumbPlaceholder>
                            )}
                          </FilePreviewButton>
                          <FileName>{getFileName(file)}</FileName>
                          <FileActions>
                            <SecondaryButton
                              type='button'
                              onClick={e => handlePreview(e, file)}
                            >
                              <VisibilityOutlinedIcon
                                style={{ fontSize: 14 }}
                              />
                              Preview
                            </SecondaryButton>
                            <SecondaryButton
                              type='button'
                              onClick={e => handleDownload(e, file)}
                            >
                              <DownloadOutlinedIcon style={{ fontSize: 14 }} />
                              Download
                            </SecondaryButton>
                          </FileActions>
                        </FileCard>
                      ))}
                    </FilesGrid>
                  ) : (
                    <MissingNote>No uploaded file linked yet.</MissingNote>
                  )}
                </EntryCard>
              )
            })}
            {visibleEntries.length === 0 && (
              <MissingNote>
                No assigned checklist entries yet. Add assignees from Settings
                first.
              </MissingNote>
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  )
}

export default ChecklistOverviewSummary

const SummaryWrap = styled.div`
  margin-top: ${({ $compact }) => ($compact ? '6px' : '12px')};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
`

const SummaryMetaButton = styled.button`
  border: none;
  border-radius: 999px;
  background: rgba(64, 163, 196, 0.14);
  padding: 5px 12px;
  color: #1d6f8f;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  text-align: left;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: #16596f;
    background: rgba(64, 163, 196, 0.22);
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`

const getProgressColor = progress => {
  if (progress >= 100) return { bg: 'rgba(61, 179, 122, 0.14)', color: '#208553' }
  if (progress > 0) return { bg: 'rgba(255, 170, 0, 0.14)', color: '#b27300' }
  return { bg: 'rgba(204, 34, 0, 0.1)', color: '#b00020' }
}

const FilesCount = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 12px;
  background: ${({ $progress = 0 }) => getProgressColor($progress).bg};
  color: ${({ $progress = 0 }) => getProgressColor($progress).color};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

const SecondaryButton = styled.button`
  border: 1px solid #d8dde6;
  border-radius: 999px;
  background: #fff;
  color: #172033;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OverviewIntro = styled.div`
  color: #6f7785;
  font-size: 13px;
  line-height: 1.5;
`

const HiddenSummary = styled.div`
  color: #6f7785;
  font-size: 13px;
  line-height: 1.5;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f7f9fc;
`

const EntryCard = styled.div`
  border: 1px solid #ebedf1;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EntryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const EntryTitle = styled.div`
  color: #172033;
  font-size: 17px;
  font-weight: 600;
`

const StatusPill = styled.span`
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
  ${({ $uploaded }) =>
    $uploaded
      ? `
        background: #e8f7ef;
        color: #1f8f56;
      `
      : `
        background: #fff3e6;
        color: #d16d14;
      `}
`

const EntryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  color: #6f7785;
  font-size: 13px;
`

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const TagChip = styled.span`
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f6fb;
  color: #33415d;
  font-size: 11px;
  font-weight: 600;
`

const EntryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`

const FileCard = styled.div`
  border: 1px solid #ebedf1;
  border-radius: 14px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const FilePreviewButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`

const FileThumb = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid #ebedf1;
`

const FileThumbPlaceholder = styled.div`
  width: 100%;
  height: 140px;
  border-radius: 12px;
  border: 1px dashed #d8dde6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7f8796;
  font-size: 12px;
`

const FileName = styled.div`
  color: #172033;
  font-size: 13px;
  font-weight: 600;
  word-break: break-word;
`

const FileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const MissingNote = styled.div`
  color: #9b6072;
  font-size: 12px;
`
